import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeInsulation, DEFAULT_INSULATION } from '../calculators/insulation'
import type { InsulationInputs, InsulationArea, InsulationApplication, RValue, StudSpacing } from '../calculators/insulation'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/insulation/'
const CATEGORY = 'roofing-and-insulation'

const R_VALUE_OPTIONS: RValue[] = ['R-13', 'R-15', 'R-19', 'R-21', 'R-30', 'R-38', 'R-49']

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  applicationLabel: string
  wallBatts: string
  ceilingBatts: string
  blownIn: string
  atticSection: string
  wallCeilingSection: string
  addSection: string
  targetRValue: string
  rValueTip: string
  studSpacing: string
  studTip: string
  studOptions: { v: StudSpacing; l: string }[]
  addVaporBarrier: string
  vaporTip: string
  wasteTip: string
  netSqft: string
  withWaste: string
  bagsWord: string
  primaryName: string
  battLabel: string
  blownLabel: string
  savePrefix: string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  mistakes: string[]
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Insulation Calculator — Batts and Blown-In by R-Value | RenoSheets',
  desc: 'Free insulation calculator for wall batts, ceiling batts and blown-in. Enter area and target R-value for bag count plus vapor barrier and tools. No signup.',
  h1: 'Insulation Calculator',
  intro: 'Enter area and target R-value for batt or blown-in bag count — plus vapor barrier and safety gear.',
  applicationLabel: 'Application',
  wallBatts: 'Wall Batts',
  ceilingBatts: 'Ceiling Batts',
  blownIn: 'Blown-In',
  atticSection: 'Attic section',
  wallCeilingSection: 'Wall / ceiling section',
  addSection: '+ Add another section',
  targetRValue: 'Target R-value',
  rValueTip: 'Walls: R-13 (2x4) or R-19/R-21 (2x6). Ceilings/attics: R-30 to R-49. Higher R = more insulation = better energy performance.',
  studSpacing: 'Stud spacing',
  studTip: '16 in o.c. is standard residential framing. 24 in o.c. is used in advanced framing (OVE). Check your actual framing before ordering.',
  studOptions: [{ v: 16, l: '16 in o.c.' }, { v: 24, l: '24 in o.c.' }],
  addVaporBarrier: 'Add vapor barrier (6-mil poly)',
  vaporTip: 'Required by code in cold climates. Install on the warm side of the insulation (interior side of wall, below attic insulation). Ask your building inspector if unsure.',
  wasteTip: '5–10% covers cuts around outlets, pipes and irregular areas. Blown-in: 10% accounts for settling.',
  netSqft: 'sq ft net',
  withWaste: 'sq ft with waste',
  bagsWord: 'bags',
  primaryName: 'Bags',
  battLabel: 'batt insulation',
  blownLabel: 'blown-in insulation',
  savePrefix: 'Insulation',
  aboutH2: 'What actually affects how much insulation you need',
  aboutP1: 'R-value requirements vary by climate zone, not personal preference — building codes set minimums based on how cold your region gets in winter, and using a lower R-value than your zone requires can fail an inspection even if it feels adequately insulated. Batt insulation is straightforward to estimate since it covers a fixed area per bag, but coverage assumes standard 16-inch on-center framing; 24-inch spacing needs fewer, wider batts, and irregular framing wastes more to cuts and fitting around obstructions. Blown-in insulation coverage is listed by depth, not just area, because settled depth matters for the final R-value — freshly blown insulation sits higher than its settled depth, so manufacturers publish both figures and you should budget for the settled number, not the fluffy just-installed look. A vapor barrier isn\'t optional in most cold-climate applications: it has to go on the warm side of the insulation, and getting the orientation backwards traps moisture inside the wall cavity instead of keeping it out.',
  howH2: 'How to calculate insulation bags',
  howP1: 'Batt insulation bags cover a fixed area at a given R-value; blown-in coverage is listed on each bag:',
  formula: `— Batts (e.g. R-19, 16 in o.c.):
  bags = ceil(area with waste / coverage per bag)
  coverage: ~38–40 sq ft per bag (varies by product)

— Blown-in (e.g. R-38 at 16 bags/1000 sq ft):
  bags = ceil(area with waste / 1000 * bags_per_1000)`,
  howP2: 'Coverage values are calibrated to published Owens Corning and CertainTeed SKUs. Always verify with the coverage chart on the actual bag — coverage varies by manufacturer.',
  mistakesH3: 'Common mistakes',
  mistakes: [
    'Compressing batts to fit — compression reduces R-value. If it doesn\'t fit without force, trim it.',
    'Leaving air gaps around wires, pipes and outlets — seal all penetrations with caulk or foam before installing batts.',
    'Installing vapor barrier on the wrong side — always on the warm side (interior in cold climates).',
    'Skipping PPE — fiberglass fibres cause skin, eye and lung irritation. Always wear gloves, long sleeves, safety glasses and an N95 mask.',
  ],
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: area with waste divided by coverage per bag; blown-in from bags-per-1000-sq-ft curves. Last updated 2026-05-24. Estimates only — verify bag coverage on the product label before purchasing.',
  faqs: [
    { q: 'What R-value do I need for exterior walls?', a: 'R-13 to R-15 for 2×4 walls; R-19 to R-21 for 2×6 walls. The International Energy Conservation Code (IECC) requires R-20 for 2×6 walls in most cold-climate zones and R-13 for warmer zones. Check your local building code for the exact requirement for your climate zone.' },
    { q: 'What R-value do I need for an attic?', a: 'Most climate zones require R-38 to R-60 for attic insulation. IECC Climate Zone 5 (including much of the northern US and Canada) typically requires R-49. Climate Zones 1–3 in the south may only require R-30. Adding more insulation than the minimum almost always has a positive ROI in energy savings.' },
    { q: 'How many bags of blown-in insulation do I need for R-38 in a 500 sq ft attic?', a: 'For R-38, plan on about 16 bags per 1000 sq ft of attic floor area. A 500 sq ft attic needs about 8 bags. The exact count depends on the brand — check the coverage chart printed on every bag for the settled depth and bag count at your target R-value.' },
    { q: 'What is the difference between R-13 and R-15 insulation?', a: 'R-13 and R-15 batts are both designed for 2×4 walls at 16 in on-center. R-15 uses a denser formulation — either high-density fiberglass or rockwool (mineral wool) — to pack more R-value into the same 3.5 in cavity. Rockwool R-15 also provides better sound damping and fire resistance than fiberglass.' },
    { q: 'Do I need a vapor barrier with insulation?', a: 'In cold climates (roughly Zone 5 and colder), a 6-mil poly vapor barrier on the warm side of wall insulation is required by code. In mixed climates (Zone 3–4), smart vapor retarders or vapor-permeable barriers are often better. In hot climates (Zone 1–2), you generally do not need a vapor barrier. Always check local building code.' },
    { q: 'Can I rent an insulation blower for blown-in insulation?', a: 'Yes — most big-box home improvement stores lend or rent a blower machine when you purchase a minimum number of bags (typically 10–20). Ask at the building materials desk. Renting an insulation blower separately costs about $60–100 per day. The machine is much faster than hand-packing batts for large attic areas.' },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur d’isolation — Matelas et soufflé par valeur R | RenoSheets',
  desc: 'Calculateur d’isolation gratuit pour matelas de murs, matelas de plafond et isolant soufflé. Entrez la surface et la valeur R cible pour le nombre de sacs, plus le pare-vapeur et les outils. Sans inscription.',
  h1: 'Calculateur d’isolation',
  intro: 'Entrez la surface et la valeur R cible pour le nombre de sacs de matelas ou d’isolant soufflé — plus le pare-vapeur et l’équipement de sécurité.',
  applicationLabel: 'Application',
  wallBatts: 'Matelas de murs',
  ceilingBatts: 'Matelas de plafond',
  blownIn: 'Soufflé',
  atticSection: 'Section de grenier',
  wallCeilingSection: 'Section de mur / plafond',
  addSection: '+ Ajouter une section',
  targetRValue: 'Valeur R cible',
  rValueTip: 'Murs : R-13 (2x4) ou R-19/R-21 (2x6). Plafonds/greniers : R-30 à R-49. Une valeur R plus élevée = plus d’isolant = meilleure performance énergétique.',
  studSpacing: 'Espacement des montants',
  studTip: '16 po entraxe est la charpente résidentielle standard. 24 po entraxe sert dans la charpente optimisée (OVE). Vérifiez votre charpente réelle avant de commander.',
  studOptions: [{ v: 16, l: '16 po entraxe' }, { v: 24, l: '24 po entraxe' }],
  addVaporBarrier: 'Ajouter un pare-vapeur (polyéthylène 6 mil)',
  vaporTip: 'Exigé par le code en climat froid. À poser du côté chaud de l’isolant (côté intérieur du mur, sous l’isolant du grenier). Demandez à votre inspecteur en bâtiment en cas de doute.',
  wasteTip: '5–10 % couvrent les coupes autour des prises, des tuyaux et des zones irrégulières. Soufflé : 10 % tient compte du tassement.',
  netSqft: 'pi² net',
  withWaste: 'pi² avec perte',
  bagsWord: 'sacs',
  primaryName: 'Sacs',
  battLabel: 'isolant en matelas',
  blownLabel: 'isolant soufflé',
  savePrefix: 'Isolation',
  aboutH2: 'Ce qui influence vraiment la quantité d’isolant nécessaire',
  aboutP1: 'Les exigences de valeur R varient selon la zone climatique, pas selon la préférence personnelle — les codes du bâtiment fixent des minimums selon le froid de votre région en hiver, et utiliser une valeur R plus basse que ce qu’exige votre zone peut faire échouer une inspection même si l’isolation semble suffisante. L’isolant en matelas est simple à estimer puisqu’il couvre une surface fixe par sac, mais la couverture suppose une ossature standard à 16 pouces centre à centre; un espacement de 24 pouces demande moins de matelas, mais plus larges, et une ossature irrégulière gaspille davantage en coupes et en ajustements autour des obstacles. La couverture de l’isolant soufflé est indiquée par profondeur, pas seulement par surface, car la profondeur tassée détermine la valeur R finale — l’isolant fraîchement soufflé est plus haut que sa profondeur tassée, alors les fabricants publient les deux chiffres et vous devriez prévoir selon le chiffre tassé, pas l’apparence gonflée tout juste posée. Un pare-vapeur n’est pas optionnel dans la plupart des applications en climat froid : il doit être posé du côté chaud de l’isolant, et inverser l’orientation emprisonne l’humidité dans la cavité du mur plutôt que de la garder à l’extérieur.',
  howH2: 'Comment calculer le nombre de sacs d’isolant',
  howP1: 'Les sacs de matelas isolants couvrent une surface fixe à une valeur R donnée; la couverture de l’isolant soufflé est indiquée sur chaque sac :',
  formula: `— Matelas (p. ex. R-19, 16 po entraxe) :
  sacs = arrondi sup.(surface avec perte / couverture par sac)
  couverture : ~38–40 pi² par sac (varie selon le produit)

— Soufflé (p. ex. R-38 à 16 sacs/1000 pi²) :
  sacs = arrondi sup.(surface avec perte / 1000 × sacs_par_1000)`,
  howP2: 'Les valeurs de couverture sont calibrées sur les codes de produit publiés par Owens Corning et CertainTeed. Vérifiez toujours avec le tableau de couverture du sac réel — la couverture varie selon le fabricant.',
  mistakesH3: 'Erreurs fréquentes',
  mistakes: [
    'Comprimer les matelas pour les faire entrer — la compression réduit la valeur R. S’ils n’entrent pas sans forcer, taillez-les.',
    'Laisser des espaces d’air autour des fils, tuyaux et prises — scellez toutes les pénétrations au scellant ou à la mousse avant de poser les matelas.',
    'Poser le pare-vapeur du mauvais côté — toujours du côté chaud (intérieur en climat froid).',
    'Sauter l’équipement de protection — les fibres de verre irritent la peau, les yeux et les poumons. Portez toujours des gants, des manches longues, des lunettes de sécurité et un masque N95.',
  ],
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : surface avec perte divisée par la couverture par sac; soufflé d’après les courbes de sacs par 1000 pi². Dernière mise à jour 2026-05-24. Estimations seulement — vérifiez la couverture du sac sur l’étiquette du produit avant d’acheter.',
  faqs: [
    { q: 'Quelle valeur R me faut-il pour les murs extérieurs?', a: 'R-13 à R-15 pour les murs 2×4; R-19 à R-21 pour les murs 2×6. Le Code international de conservation de l’énergie (IECC) exige R-20 pour les murs 2×6 dans la plupart des zones de climat froid et R-13 dans les zones plus chaudes. Vérifiez votre code du bâtiment local pour l’exigence exacte de votre zone climatique.' },
    { q: 'Quelle valeur R me faut-il pour un grenier?', a: 'La plupart des zones climatiques exigent R-38 à R-60 pour l’isolant de grenier. La zone climatique 5 de l’IECC (incluant une grande partie du nord des États-Unis et du Canada) exige généralement R-49. Les zones climatiques 1 à 3 du sud peuvent n’exiger que R-30. Ajouter plus d’isolant que le minimum offre presque toujours un rendement positif en économies d’énergie.' },
    { q: 'Combien de sacs d’isolant soufflé me faut-il pour R-38 dans un grenier de 500 pi²?', a: 'Pour R-38, prévoyez environ 16 sacs par 1000 pi² de surface de plancher du grenier. Un grenier de 500 pi² demande environ 8 sacs. Le nombre exact dépend de la marque — consultez le tableau de couverture imprimé sur chaque sac pour l’épaisseur tassée et le nombre de sacs à votre valeur R cible.' },
    { q: 'Quelle est la différence entre les isolants R-13 et R-15?', a: 'Les matelas R-13 et R-15 sont tous deux conçus pour les murs 2×4 à 16 po entraxe. Le R-15 utilise une formulation plus dense — fibre de verre haute densité ou laine de roche (laine minérale) — pour concentrer plus de valeur R dans la même cavité de 3,5 po. La laine de roche R-15 offre aussi une meilleure insonorisation et résistance au feu que la fibre de verre.' },
    { q: 'Ai-je besoin d’un pare-vapeur avec l’isolant?', a: 'En climat froid (environ la zone 5 et plus froid), un pare-vapeur en polyéthylène 6 mil du côté chaud de l’isolant des murs est exigé par le code. En climat mixte (zones 3–4), les pare-vapeur intelligents ou perméables à la vapeur sont souvent préférables. En climat chaud (zones 1–2), un pare-vapeur n’est généralement pas nécessaire. Vérifiez toujours le code du bâtiment local.' },
    { q: 'Puis-je louer une souffleuse pour l’isolant soufflé?', a: 'Oui — la plupart des grandes surfaces de rénovation prêtent ou louent une souffleuse à l’achat d’un nombre minimal de sacs (généralement 10 à 20). Informez-vous au comptoir des matériaux de construction. Louer une souffleuse à isolant séparément coûte environ 60–100 $ par jour. La machine est beaucoup plus rapide que de poser les matelas à la main pour les grandes surfaces de grenier.' },
  ],
}

export default function InsulationCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<InsulationInputs>(DEFAULT_INSULATION)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeInsulation(i, locale), [i, locale])
  const set = (patch: Partial<InsulationInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }
  const setArea = (idx: number, patch: Partial<InsulationArea>) =>
    set({ areas: i.areas.map((a, k) => (k === idx ? { ...a, ...patch } : a)) })
  const addArea = () => set({ areas: [...i.areas, { length: 12, width: 9 }] })
  const removeArea = (idx: number) => set({ areas: i.areas.filter((_, k) => k !== idx) })
  const lin = i.unit === 'imperial' ? (locale === 'fr' ? 'pi' : 'ft') : 'm'

  const applicationOptions: { v: InsulationApplication; l: string }[] = [
    { v: 'wall-batts', l: c.wallBatts },
    { v: 'ceiling-batts', l: c.ceilingBatts },
    { v: 'blown-in', l: c.blownIn },
  ]

  const onSave = async () => {
    await saveSheet({
      id: genId(),
      type: 'insulation',
      name: `${c.savePrefix} — ${r.totalArea} ${c.netSqft}`,
      createdAt: new Date().toISOString(),
      summary: `${r.bags} ${c.bagsWord} · ${i.rValue} · ${i.application}`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'insulation')

  const applicationLabelFor = i.application === 'blown-in' ? c.blownLabel : c.battLabel
  const primaryLabel = locale === 'fr'
    ? `de ${applicationLabelFor} ${i.rValue}`
    : `of ${i.rValue} ${applicationLabelFor}`
  const assumptions = locale === 'fr'
    ? `${r.totalArea} pi² · ${i.rValue} · ${i.application} · ${i.wastePct} % de perte.`
    : `${r.totalArea} sq ft · ${i.rValue} · ${i.application} · ${i.wastePct}% waste.`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeInsulation: sum areas, × (1 + waste%), then either ÷ coverage
  // per bag (batts) or × (bags per 1000) ÷ 1000 (blown-in).
  // Coverage values must match the BATT_COVERAGE / BLOWN_IN_BAGS_PER_1000 tables
  // in src/calculators/insulation.ts.
  const sqftWord = locale === 'fr' ? 'pi²' : 'sq ft'
  const BATT_COVERAGE_LOCAL: Record<string, Record<number, number>> = {
    'R-13': { 16: 40, 24: 48 }, 'R-15': { 16: 40, 24: 48 }, 'R-19': { 16: 40, 24: 48 },
    'R-21': { 16: 38, 24: 46 }, 'R-30': { 16: 35, 24: 40 }, 'R-38': { 16: 32, 24: 38 },
    'R-49': { 16: 30, 24: 36 },
  }
  const BLOWN_PER_1000: Record<string, number> = {
    'R-13': 6, 'R-15': 7, 'R-19': 9, 'R-21': 10, 'R-30': 12, 'R-38': 16, 'R-49': 21,
  }
  const buildExplain = (): React.ReactNode => {
    const isBlown = i.application === 'blown-in'
    const coverage = isBlown ? 0 : (BATT_COVERAGE_LOCAL[i.rValue]?.[i.studSpacing] ?? 40)
    const bagsPer1000 = isBlown ? (BLOWN_PER_1000[i.rValue] ?? 12) : 0
    return (
      <ol className="list-decimal space-y-1 pl-5">
        <li>
          {i.inputMode === 'area'
            ? (locale === 'fr' ? 'Surface saisie' : 'Area you entered')
            : (locale === 'fr'
                ? `Surface totale (${i.areas.length} ${i.areas.length === 1 ? 'section' : 'sections'})`
                : `Total area (${i.areas.length} ${i.areas.length === 1 ? 'section' : 'sections'})`)}:
          {' '}<strong>{r.totalArea} {sqftWord}</strong>
        </li>
        <li>{locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}: <strong>{r.areaWithWaste} {sqftWord}</strong></li>
        {isBlown ? (
          <li>
            {locale === 'fr'
              ? `× ${bagsPer1000} sacs / 1000 pi² (${i.rValue})`
              : `× ${bagsPer1000} bags / 1000 sq ft (${i.rValue})`}
            {' '}= <strong>{((r.areaWithWaste / 1000) * bagsPer1000).toFixed(1)}</strong>
          </li>
        ) : (
          <li>
            {locale === 'fr'
              ? `÷ ${coverage} pi²/sac (${i.rValue}, ${i.studSpacing} po entraxe)`
              : `÷ ${coverage} sq ft/bag (${i.rValue}, ${i.studSpacing} in o.c.)`}
            {' '}= <strong>{(r.areaWithWaste / Math.max(1, coverage)).toFixed(1)}</strong>
          </li>
        )}
        <li>{locale === 'fr' ? 'Arrondi au sac entier' : 'Rounded up to whole bags'} = <strong>{r.bags} {locale === 'fr' ? (r.bags === 1 ? 'sac' : 'sacs') : (r.bags === 1 ? 'bag' : 'bags')}</strong></li>
      </ol>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('insulation', 'Insulation Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Roofing & Insulation', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('insulation', 'Insulation Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-5 flex flex-wrap gap-6">
            <Segmented
              label={tr('calc.input')}
              value={i.inputMode}
              onChange={(v) => {
                if (v === 'area' && i.inputMode === 'dimensions') {
                  const sum = i.areas.reduce((s, a) => s + Math.max(0, a.length) * Math.max(0, a.width), 0)
                  set({ inputMode: v, totalArea: Math.round(sum * 10) / 10 })
                } else {
                  set({ inputMode: v })
                }
              }}
              options={[{ v: 'dimensions', l: tr('calc.dimensions') }, { v: 'area', l: tr('calc.totalArea') }]}
            />
          </div>

          {i.inputMode === 'dimensions' ? (
            <div className="space-y-3">
              {i.areas.map((a, idx) => (
                <div key={idx} className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-text-body">
                      {i.application === 'blown-in' ? c.atticSection : c.wallCeilingSection} {idx + 1}
                    </span>
                    {i.areas.length > 1 && (
                      <button type="button" onClick={() => removeArea(idx)} className="text-xs text-danger hover:underline">{tr('calc.remove')}</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <SmartField label={tr('calc.length')} imperialValue={a.length} onChangeImperial={(n) => setArea(idx, { length: n })} imperialUnit="ft" />
                    <SmartField label={tr('calc.width')} imperialValue={a.width} onChangeImperial={(n) => setArea(idx, { width: n })} imperialUnit="ft" />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addArea} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
                {c.addSection}
              </button>
            </div>
          ) : (
            <div className="rounded-md border border-border p-3">
              <SmartField
                label={tr('calc.totalArea')}
                imperialValue={i.totalArea}
                onChangeImperial={(n) => set({ totalArea: n })}
                imperialUnit="sq ft"
              />
              <p className="mt-2 text-xs text-text-subtle">{tr('calc.areaHint')}</p>
            </div>
          )}

          {/* Application */}
          <div className="mt-4">
            <Segmented
              label={c.applicationLabel}
              value={i.application}
              onChange={(v) => set({ application: v as InsulationApplication })}
              options={applicationOptions}
            />
          </div>

          {/* R-value */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.targetRValue}
              <InfoTip text={c.rValueTip} />
            </span>
            <select
              value={i.rValue}
              onChange={(e) => set({ rValue: e.target.value as RValue })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2.5 text-sm text-text focus:border-brand focus:outline-none"
            >
              {R_VALUE_OPTIONS.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          {/* Stud spacing (batts only) */}
          {i.application !== 'blown-in' && (
            <div className="mt-4">
              <span className="mb-1 flex items-center text-sm font-medium text-text-body">
                {c.studSpacing}
                <InfoTip text={c.studTip} />
              </span>
              <div className="flex gap-4">
                {c.studOptions.map((o) => (
                  <label key={o.v} className="flex items-center gap-1.5 text-sm text-text-body">
                    <input type="radio" checked={i.studSpacing === o.v} onChange={() => set({ studSpacing: o.v as StudSpacing })} />
                    {o.l}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Vapor barrier */}
          <label className="mt-4 flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={i.needVaporBarrier} onChange={(e) => set({ needVaporBarrier: e.target.checked })} />
            {c.addVaporBarrier}
            <InfoTip text={c.vaporTip} />
          </label>

          {/* Waste */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={5} max={15} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">
              {r.totalArea} {c.netSqft} &rarr; {r.areaWithWaste} {c.withWaste} &rarr; {r.bags} {c.bagsWord}
            </p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: c.primaryName, label: primaryLabel, qty: r.bags, unit: 'bag', query: `${i.rValue} insulation batts` }}
            secondary={null}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {c.mistakes.map((m, k) => <li key={k}>{m}</li>)}
        </ul>
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
